"use client"

/**
 * The membership-first site's footer (Eugene, 30.09): today's footer
 * (`./footer.tsx`) in a tidier layout. A narrower brand column so the link
 * columns rarely wrap, the contact lines as one group, one rhythm for every
 * list, and a bottom bar of whole labels with no dots. Same props and the same
 * columns from `useLocalizedNavigation`.
 *
 * PageShell renders it on the membership-first site and today's footer
 * everywhere else, so the live site is unchanged while the release flag is
 * off. After launch it replaces `./footer.tsx`.
 */

import Link from "next/link"
import { ConsentDetailsLink } from "@ahead-health/core/ui/cookieConsent"
import { cn } from "@/lib/utils"
import { track } from "@/lib/analytics"
import { PhoneIcon, StarIcon, GoogleIcon } from "@/components/ui/icons"
import type { FooterProps } from "@/components/ui/footer"

/* ---------- Inline SVG icons ---------- */

function GoogleStars() {
  return (
    <a
      href="https://www.google.com/maps/place/Ahead+Health/@47.3734617,8.5311033,17z/data=!4m8!3m7!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp"
      target="_blank"
      rel="noopener noreferrer nofollow"
      aria-label="Ahead Health 5-star rating on Google"
      className="flex w-fit items-center gap-2 transition-opacity hover:opacity-70"
    >
      {/* Google "G" */}
      <GoogleIcon width={16} height={16} aria-hidden="true" />
      {/* 5 yellow stars. The star glyph sits inside its box with room to
          spare, so they are set edge to edge. */}
      <span className="flex items-center">
        {Array.from({ length: 5 }).map((_, i) => (
          <StarIcon key={i} width={17} height={17} aria-hidden="true" />
        ))}
      </span>
    </a>
  )
}

function MailIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  )
}

function WhatsAppIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  )
}

function LinkedInIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function InstagramIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 1 0 0-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 0 1-2.88 0 1.441 1.441 0 0 1 2.88 0z" />
    </svg>
  )
}

function FacebookIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

const socialIconMap = {
  linkedin: LinkedInIcon,
  instagram: InstagramIcon,
  facebook: FacebookIcon,
} as const

/* ---------- Footer component ---------- */

// One class for every contact line.
const CONTACT_CLS =
  "hover:text-aubergine-600 type-body-md-emphasized flex w-fit items-center gap-2.5 text-neutral-500 transition-colors"

export function MembershipFirstFooter({
  columns,
  logo,
  tagline,
  phone,
  email,
  whatsappHref,
  whatsappLabel,
  socialLinks,
  copyright,
  legalLinks,
  cookieSettingsLabel,
  className,
  localePicker,
  crossBorderNotice,
  locale,
}: FooterProps) {
  return (
    <footer
      className={cn("bg-neutral-100 px-8 py-12 md:px-10 md:py-20", className)}
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-10 xl:grid-cols-[minmax(0,300px)_minmax(0,1fr)] xl:gap-24">
          {/* Left column — branding + contact */}
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              {logo && <div>{logo}</div>}
              {tagline && (
                <p className="type-body-xl-emphasized text-pretty text-neutral-900">
                  {tagline}
                </p>
              )}
            </div>

            <GoogleStars />

            <div className="flex flex-col gap-3">
              {phone && (
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  onClick={() =>
                    track.externalLinkClick(
                      `tel:${phone.replace(/\s/g, "")}`,
                      "Phone"
                    )
                  }
                  className={CONTACT_CLS}
                >
                  <PhoneIcon />
                  {phone}
                </a>
              )}
              {email && (
                <a
                  href={`mailto:${email}`}
                  onClick={() =>
                    track.externalLinkClick(`mailto:${email}`, "Email")
                  }
                  className={CONTACT_CLS}
                >
                  <MailIcon />
                  {email}
                </a>
              )}
              {whatsappHref && (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onClick={() =>
                    track.externalLinkClick(whatsappHref, "WhatsApp")
                  }
                  className={CONTACT_CLS}
                >
                  <WhatsAppIcon />
                  {whatsappLabel || "Start a WhatsApp chat"}
                </a>
              )}
            </div>

            {socialLinks && socialLinks.length > 0 && (
              <div className="flex items-center gap-3">
                {socialLinks.map((link) => {
                  const Icon = socialIconMap[link.platform]
                  return (
                    <a
                      key={link.platform}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="hover:text-aubergine-600 text-neutral-900 transition-colors"
                      aria-label={`Ahead Health on ${link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}`}
                    >
                      <Icon />
                    </a>
                  )
                })}
              </div>
            )}

            {crossBorderNotice}
          </div>

          {/* Right — link columns. As wide as their longest link while there
              is room, spread across the row, so few labels wrap. */}
          <div className="grid grid-cols-2 gap-8 gap-y-8 sm:grid-cols-3 lg:grid-cols-[repeat(4,minmax(0,max-content))] lg:justify-between lg:gap-x-10">
            {columns.map((column) => (
              <div key={column.title}>
                <p className="type-body-md-emphasized mb-4 text-neutral-500">
                  {column.title}
                </p>
                <ul className="flex flex-col gap-2">
                  {column.links.map((link) => {
                    // Shared label + badge. Single badge style across the
                    // footer: blue pill, sentence case, no letter-spacing.
                    // `type-label-sm` bakes in `uppercase` + `letter-spacing`
                    // at the @utility level, and Tailwind v4's cascade lets it
                    // beat the `!normal-case` / `!tracking-normal` modifiers —
                    // so we override both with inline style instead. Badge
                    // values in i18n are already authored in sentence case
                    // ("New", "Hiring", "Neu", "Nouveau").
                    const inner = (
                      <>
                        {link.label}
                        {link.badge && (
                          <span
                            className="type-label-sm bg-bright-blue-700 ml-1.5 inline-block rounded-full px-1.5 py-0.5 align-middle font-medium whitespace-nowrap text-white"
                            style={{
                              textTransform: "none",
                              letterSpacing: "0",
                              // Lift the pill 2px so its visual midline
                              // sits a hair above the label baseline —
                              // pure align-middle ends up looking
                              // bottom-heavy next to the larger text.
                              transform: "translateY(-2px)",
                            }}
                          >
                            {link.badge}
                          </span>
                        )}
                      </>
                    )
                    // Element is inline (NOT inline-flex) so the badge
                    // participates in the text's line-wrap. Dutch only:
                    // `break-words` lets long compounds wrap inside the
                    // narrow 2-col mobile grid (see ./footer.tsx).
                    const className = cn(
                      "hover:text-aubergine-600 type-body-md-emphasized text-neutral-900 transition-colors",
                      locale === "nl" && "break-words"
                    )
                    return (
                      <li key={link.href ?? link.label}>
                        {link.onClick ? (
                          <button
                            type="button"
                            onClick={link.onClick}
                            className={cn(
                              "cursor-pointer text-left",
                              className
                            )}
                          >
                            {inner}
                          </button>
                        ) : (
                          <Link href={link.href!} className={className}>
                            {inner}
                          </Link>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar — copyright + legal + (optional) locale picker. One
            wrapping row of whole labels, no dots, so phones get tidy lines
            instead of split labels and stray separators. */}
        <div className="mt-10 flex flex-col items-start gap-4 border-t border-neutral-200 pt-6 md:mt-16 lg:flex-row lg:items-center lg:gap-6">
          {copyright && (
            <p className="type-body-md text-neutral-500">{copyright}</p>
          )}
          <div className="type-body-md flex flex-wrap items-center gap-x-6 gap-y-2 text-neutral-500">
            {legalLinks?.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="hover:text-aubergine-600 whitespace-nowrap transition-colors"
              >
                {link.label}
              </Link>
            ))}
            {cookieSettingsLabel && (
              <ConsentDetailsLink className="hover:text-aubergine-600 whitespace-nowrap transition-colors">
                {cookieSettingsLabel}
              </ConsentDetailsLink>
            )}
          </div>
          {localePicker && <div className="lg:ml-auto">{localePicker}</div>}
        </div>
      </div>
    </footer>
  )
}
